import type { FormikErrors, FormikTouched } from 'formik'
import { Field, Formik } from 'formik'
import React from 'react'

import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { PageLayout } from '@components/common/PageLayout'
import { DashboardBackButton } from '@components/utils'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  ButtonClean,
  Checkbox,
  Fieldset,
  HeadContent,
  Input,
  InputUI,
  Loading,
  RadioButton,
  Skeleton,
  Subsection,
  Tag,
} from '@fourthwall/components'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import { ConnectBar } from '../../components/ConnectBar/ConnectBar'
import { mapStatusToTagProps } from '../../utils/mapStatusToTagProps'
import * as S from './AppsTwitchDiscounts.styled'
import type { FormValues } from './AppsTwitchDiscounts.types'
import { StatusTag } from './components/StatusTag/StatusTag'
import { useTwitchDiscounts } from './hooks/useTwitchDiscounts'

export const AppsTwitchDiscounts = () => {
  const { confirmBarProps, connectBarProps, twitchStatus, form, memberships, shop, isLoading } =
    useTwitchDiscounts()

  const renderStatusTag = () => {
    const props = mapStatusToTagProps(twitchStatus)

    if (!props) return null

    return (
      <div style={{ marginBottom: 20 }}>
        <Tag {...props} size="large" display="inline-flex" />
      </div>
    )
  }

  const disabledSuggestion = (
    <Fieldset.Row inner>
      <S.SuggestionContainer>
        <InputUI.Suggestion>
          Discount amount cannot be changed with members actively using the promotion
        </InputUI.Suggestion>
      </S.SuggestionContainer>
    </Fieldset.Row>
  )

  const renderShopForm = () => {
    if (!shop.shouldRenderForm) return null

    return (
      <Subsection title="Shop discounts">
        <S.SectionDescription>
          <p>
            To create shop discounts for Twitch subs:
            <ol>
              <li>
                <ButtonClean
                  label="Create a new auto-applied promotion"
                  target="_blank"
                  to={`${routing.promotions.promotionCodes.create.self}?mode=twitch`}
                />{' '}
                in Promotions tab
              </li>
              <li>
                Select “Make this promotion available only for your Twitch subs” option in the
                Requirements section.
              </li>
            </ol>
          </p>
          <p>
            Supporters with eligible products in their cart will see promotion banner at the top of
            the checkout where they can connect to Twitch to verify their subscription.{' '}
            <ButtonClean
              label="Learn more"
              href={routing.external.helpCenter.twitchShopDiscounts}
              target="_blank"
            />
          </p>
        </S.SectionDescription>
      </Subsection>
    )
  }

  const renderMembershipsForm = (
    values: FormValues,
    errors: FormikErrors<FormValues>,
    touched: FormikTouched<FormValues>,
  ) => {
    if (!memberships.shouldRenderForm) return null

    const renderTiers = () => {
      if (values.accessType == 'AllTiers') return null

      return Object.keys(values.tiers).map((key) => {
        const tierId = parseInt(key)
        const tier = values.tiers[tierId]

        return (
          <>
            <Fieldset.Row inner>
              <Field
                component={Checkbox}
                name={`tiers.${key}.enabled`}
                label={tier.name}
                hideSpinner
                disabled={!tier.editable}
              />
            </Fieldset.Row>
            {values.tiers?.[tierId].enabled && (
              <>
                <Fieldset.Row inner>
                  <S.PercentageInputContainer>
                    <Field
                      component={Input}
                      name={`tiers.${key}.percentageOff`}
                      label="Percentage off"
                      type="number"
                      append="%"
                      hideSpinner
                      disabled={!tier.editable}
                    />
                  </S.PercentageInputContainer>
                </Fieldset.Row>
                <S.PercentageInputContainer>
                  {!tier.editable && disabledSuggestion}
                </S.PercentageInputContainer>
              </>
            )}
          </>
        )
      })
    }

    return (
      <Subsection title="Membership discounts">
        {memberships.statusTagProps && <StatusTag {...memberships.statusTagProps} />}
        <S.SectionDescription>
          Supporters will see promotion banner at the top of the membership checkout. Once members
          connect their accounts, the discount will apply as long as they remained subscribed on
          Twitch.{' '}
          <ButtonClean
            label="Learn more"
            href={routing.external.helpCenter.twitchMembershipsIntegrations}
            target="_blank"
          />
        </S.SectionDescription>
        <Fieldset>
          <Fieldset.Row>
            <Field
              name="accessType"
              component={RadioButton}
              label="Offer the same discount for all tiers"
              checkedValue="AllTiers"
              disabled={memberships.hasActiveSubscriptions}
            />
          </Fieldset.Row>
          {values.accessType === 'AllTiers' && (
            <>
              <Fieldset.Row inner>
                <Field
                  component={Input}
                  name="percentageOff"
                  label="Percentage off"
                  type="number"
                  append="%"
                  hideSpinner
                  disabled={!values.editable}
                />
              </Fieldset.Row>
              {!values.editable && disabledSuggestion}
            </>
          )}
          <Fieldset.Row>
            <Field
              name="accessType"
              component={RadioButton}
              label="Offer different discounts for different tiers"
              checkedValue="SelectedTiers"
              disabled={memberships.hasActiveSubscriptions}
            />
          </Fieldset.Row>

          {renderTiers()}

          {errors.tiers && touched.tiers && (
            <Fieldset.Row inner>
              {/* @ts-ignore */}
              <InputUI.Error>{errors.tiers}</InputUI.Error>
            </Fieldset.Row>
          )}
        </Fieldset>
      </Subsection>
    )
  }

  const renderForm = () => {
    return (
      <Formik<FormValues> {...form} enableReinitialize>
        {({ values, errors, touched, isValid, dirty, submitForm, resetForm }) => {
          return (
            <>
              {renderShopForm()}
              {renderMembershipsForm(values, errors, touched)}
              <ConfirmBar
                {...confirmBarProps}
                isOpen={dirty}
                onConfirm={submitForm}
                onCancel={resetForm}
              />
              <LeavingGuard when={dirty} isValid={isValid} onSave={submitForm} />
            </>
          )
        }}
      </Formik>
    )
  }

  return (
    <Loading isLoading={isLoading}>
      <HeadContent title="Discounts for Twitch subs" />
      <PageLayout size="tiny">
        <Dashboard.Section
          name="Discounts for Twitch subs"
          topContent={<DashboardBackButton label="Apps" to={routing.apps.self} />}
        >
          <S.HeaderContainer>
            {renderStatusTag()}
            <S.DescriptionWrapper>
              <p>
                Offer your Twitch subscribers discounts on shop orders or membership subscriptions.
              </p>
            </S.DescriptionWrapper>

            <div>
              {connectBarProps ? (
                <ConnectBar {...connectBarProps} />
              ) : (
                <Skeleton>
                  <Skeleton.Shape height={64} />
                </Skeleton>
              )}
            </div>
          </S.HeaderContainer>
          {renderForm()}
        </Dashboard.Section>
      </PageLayout>
    </Loading>
  )
}
